import type {Ref} from 'vue';
import type {TLoginResponce} from '~/types/user';
import {
    getAuthCodeDeliveryHint,
    getAuthCodeStepTitle,
    isAuthCodeDeliveryChannel,
    type AuthCodeDeliveryChannel,
} from '~/utils/authCodeDelivery';

export type SmsVerificationConfig = {
    phone: Ref<string>;
    captchaToken: Ref<string|undefined>;
    updateCaptcha: () => Promise<void>;
    testCaptcha: () => Promise<boolean|void>;
    verificationType: 'client-login' | 'doctor-login' | 'doctor-register' | 'clinic-register';
    onSuccess?: (data?: TLoginResponce) => void;
    onError?: (error: any) => void;
    setPhoneError?: (error: string) => void;
}

export function useSmsVerification(config: SmsVerificationConfig) {
    const {apiQuery} = useApi();
    const authStore = useAuthStore();

    const authStep = ref<number>(1);
    const pincode = ref<string>('');
    const errorPin = ref<string>('');
    const isSending = ref<boolean>(false);
    const deliveryChannel = ref<AuthCodeDeliveryChannel | null>(null);
    let deliveryChannelPhone = '';
    const mayUseApp = config.verificationType === 'client-login';
    const deliveryHint = computed(() => getAuthCodeDeliveryHint(deliveryChannel.value, mayUseApp));
    const deliveryStepTitle = computed(() => getAuthCodeStepTitle(deliveryChannel.value, mayUseApp));

    const getEndpoints = () => {
        switch (config.verificationType) {
            case 'client-login':
                return {
                    send: '/client/auth/sms/send/',
                    verify: '/client/auth/sms/login/'
                };
            case 'doctor-login':
                return {
                    send: '/doctor/auth/sms/send/',
                    verify: '/doctor/auth/sms/login/'
                };
            case 'doctor-register':
                return {
                    send: '/doctor/auth/sms/register/send/',
                    verify: '/doctor/auth/sms/register/'
                };
            case 'clinic-register':
                return {
                    send: '/clinic/auth/sms/send/',
                    verify: '/clinic/auth/sms/verify/'
                };
            default:
                throw new Error(`Unknown verification type: ${config.verificationType}`);
        }
    };

    /**
     * Текст ошибки по полю phone (массив или строка) + общий message,
     * приведённый к нижнему регистру для надёжного сравнения.
     */
    const extractErrorText = (error: any): string => {
        const phoneErrors = error?.errors?.phone;
        const phoneText = Array.isArray(phoneErrors) ? phoneErrors.join(' ') : (phoneErrors ?? '');
        return `${phoneText} ${error?.message ?? ''}`.toLowerCase();
    };

    const isAlreadySentError = (error: any): boolean => {
        if (error?.statusCode === 429) {
            return typeof error?.retryAfterSeconds === 'number'
                || extractErrorText(error).includes('уже отправлен');
        }
        const text = extractErrorText(error);
        return text.includes('уже отправлен') || text.includes('повторите через');
    };

    const isClientNetworkBlock = (error: any): boolean => {
        const message = String(error?.message ?? '').toLowerCase();

        return message.includes('failed to fetch')
            || message.includes('networkerror')
            || message.includes('load failed')
            || message.includes('откройте сайт через http://vetsy.test');
    };

    /**
     * HTTP-ответ не получен (нет statusCode): таймаут или обрыв соединения.
     * Запрос мог дойти до сервера и SMS могла уйти (частый случай в Safari
     * с Private Relay / ITP, когда теряется именно ответ). Пользователя нельзя
     * оставлять без поля ввода кода при фактически отправленной SMS.
     * CORS / Failed to fetch сюда не входят — запрос до API не дошёл.
     */
    const isResponseMissing = (error: any): boolean => {
        return error?.statusCode === undefined && !isClientNetworkBlock(error);
    };

    const resolvePhoneErrorMessage = (error: any): string => {
        const phoneErrors = error?.errors?.phone;
        if (phoneErrors) {
            return Array.isArray(phoneErrors) ? phoneErrors[0] : String(phoneErrors);
        }
        return error?.message || 'Не удалось отправить код. Попробуйте позже.';
    };

    /**
     * Send SMS code to phone number
     */
    const sendSmsCode = async (): Promise<boolean> => {
        isSending.value = true;
        errorPin.value = '';
        let wasSent = false;

        const endpoints = getEndpoints();
        const cleanPhone = config.phone.value.replace(/[^+\d]/g, '');

        await config.testCaptcha()
            .then(async () => {
                await apiQuery<{ success: boolean; deliveryChannel?: string }>(endpoints.send, 'POST', {
                    body: {
                        phone: cleanPhone,
                        recaptchaToken: config.captchaToken.value
                    },
                    // Отправка SMS неидемпотентна: авто-ретрай приводит к дублю
                    // запроса и ложной ошибке rate-limit при потерянном ответе.
                    retry: 0
                })
                    .then((data) => {
                        deliveryChannel.value = isAuthCodeDeliveryChannel(data.deliveryChannel)
                            ? data.deliveryChannel
                            : null;
                        deliveryChannelPhone = cleanPhone;

                        if (data.success && authStep.value === 1) {
                            authStep.value++;
                        }
                        wasSent = data.success;
                    })
                    .catch((error) => {
                        // Показываем поле ввода кода, если SMS уже отправлена
                        // (rate-limit) либо ответ сервера потерян, но запрос мог
                        // дойти и код мог уйти. В обоих случаях оставлять
                        // пользователя без поля ввода нельзя.
                        if (isAlreadySentError(error) || isResponseMissing(error)) {
                            if (isResponseMissing(error) || cleanPhone !== deliveryChannelPhone) {
                                deliveryChannel.value = null;
                            }
                            if (authStep.value === 1) {
                                authStep.value = 2;
                            }
                            if (isAlreadySentError(error)) {
                                errorPin.value = resolvePhoneErrorMessage(error);
                            }
                            return;
                        }
                        if (error?.statusCode === 429) {
                            const message = resolvePhoneErrorMessage(error);
                            if (authStep.value === 2) {
                                errorPin.value = message;
                            } else if (config.setPhoneError) {
                                config.setPhoneError(message);
                            } else if (config.onError) {
                                config.onError(error);
                            }
                            return;
                        }
                        if (authStep.value === 2) {
                            errorPin.value = resolvePhoneErrorMessage(error);
                        } else if (config.setPhoneError) {
                            config.setPhoneError(resolvePhoneErrorMessage(error));
                        } else if (config.onError) {
                            config.onError(error);
                        }
                    });

                await config.updateCaptcha();
                isSending.value = false;
            })
            .catch((e) => {
                if (config.onError) {
                    config.onError({message: 'Ошибка проверки на робота. Попробуйте перезагрузить страницу.'});
                }
                isSending.value = false;
            });

        return wasSent;
    };

    /**
     * Verify SMS code
     * Returns true if verification was successful, false otherwise
     */
    const verifySmsCode = async (additionalData: Record<string, any> = {}): Promise<boolean> => {
        // Повторная отправка при уже летящем запросе сжигает лишнюю попытку
        // (лимит 5) — глушим двойной клик / повторный Enter.
        if (isSending.value) {
            return false;
        }
        if (pincode.value.length !== 6) {
            errorPin.value = 'Введите 6-значный код';
            return false;
        }

        isSending.value = true;
        errorPin.value = '';

        const endpoints = getEndpoints();
        const cleanPhone = config.phone.value.replace(/[^+\d]/g, '');

        if (config.verificationType === 'client-login') {
            authStore.getFingerprint();
        }

        const guestIdPayload = config.verificationType === 'client-login' && authStore.fingerprint
            ? { guestId: authStore.fingerprint }
            : {};

        try {
            const data = await apiQuery<TLoginResponce>(endpoints.verify, 'POST', {
                body: {
                    phone: cleanPhone,
                    code: pincode.value,
                    ...additionalData,
                    ...guestIdPayload,
                },
                // Проверка кода неидемпотентна (счётчик попыток, лимит 5):
                // авто-ретрай при потерянном ответе сжёг бы лишнюю попытку.
                retry: 0
            });

            if (config.verificationType === 'clinic-register') {
                authStep.value = 3;
            } else if (data.accessToken) {
                authStore.setAccessToken(data.accessToken);
                authStore.setUserRole(config.verificationType === 'client-login' ? 'client' : 'doctor');
                authStep.value++;
            }

            if (config.onSuccess) {
                config.onSuccess(data);
            }

            await config.updateCaptcha();
            isSending.value = false;
            return true;
        } catch (error: any) {
            // Показываем в errorPin только если ошибка связана с полем code
            if (error.errors && error.errors.code) {
                errorPin.value = Array.isArray(error.errors.code) 
                    ? error.errors.code[0] 
                    : error.errors.code;
            } else if (!error.errors) {
                // Если нет объекта errors (общая ошибка), показываем message
                errorPin.value = error.message || 'Неверный код';
            } else {
                // Если есть error.errors, но там нет code - очищаем errorPin
                errorPin.value = '';
            }
            
            if (config.onError) {
                config.onError(error);
            }
            await config.updateCaptcha();
            isSending.value = false;
            return false;
        }
    };

    /**
     * Combined handler for confirmation button
     * Sends code on step 1, verifies on step 2
     */
    const confirmPhone = async (additionalData: Record<string, any> = {}): Promise<boolean> => {
        if (authStep.value === 1) {
            await sendSmsCode();
            return false;
        } else if (authStep.value === 2) {
            return await verifySmsCode(additionalData);
        }
        return false;
    };

    /**
     * Check if phone needs confirmation
     */
    const isShowNeedConfirmPhone = computed(() => {
        return !!(config.phone.value && authStep.value < 3);
    });

    /**
     * Check if verification is complete
     */
    const isVerified = computed(() => authStep.value === 3);

    /**
     * Вернуться с шага ввода кода на шаг ввода телефона. authStep экспортируется
     * readonly, поэтому кнопка «Назад» не может писать в него напрямую — сброс
     * идёт через это действие. Чистим код и ошибку, чтобы на шаге телефона не
     * осталось прежнего значения/сообщения.
     */
    const resetToPhoneStep = () => {
        authStep.value = 1;
        pincode.value = '';
        errorPin.value = '';
    };

    /**
     * Reset verification state
     */
    const resetVerification = () => {
        authStep.value = 1;
        pincode.value = '';
        errorPin.value = '';
        isSending.value = false;
        deliveryChannel.value = null;
        deliveryChannelPhone = '';
    };

    return {
        authStep: readonly(authStep),
        pincode,
        errorPin,
        isSending: readonly(isSending),
        deliveryChannel: readonly(deliveryChannel),
        deliveryHint,
        deliveryStepTitle,

        isShowNeedConfirmPhone,
        isVerified,

        sendSmsCode,
        verifySmsCode,
        confirmPhone,
        resetToPhoneStep,
        resetVerification
    };
}
